/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import type { InlineChatError } from '../../../../controllers/inline-chat-controller';
import messages from './messages';

const styles = cssMap({
	message: {
		color: token('color.text'),
	},
	actions: {
		marginLeft: 'auto',
	},
});

type ErrorViewProps = {
	error: InlineChatError | null;
	onClose: () => void;
	onTryAgain: () => void;
};

export const ErrorView = ({ error, onClose, onTryAgain }: ErrorViewProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const renderErrorMessage = () => {
		switch (error?.message_template) {
			case 'RATE_LIMIT':
				return formatMessage(messages.rateLimited, {
					a: (chunks: ReactNode[]) => {
						return (
							<Anchor
								href="https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/"
								target="_blank"
							>
								{chunks}
							</Anchor>
						);
					},
				});
			case 'ACCEPTABLE_USE_VIOLATIONS':
				return formatMessage(messages.aupViolation, {
					a: (chunks: ReactNode[]) => {
						return (
							<Anchor href="https://www.atlassian.com/legal/acceptable-use-policy" target="_blank">
								{chunks}
							</Anchor>
						);
					},
				});
			case 'TOKEN_LIMIT_EXCEEDED':
				return formatMessage(messages.tokenLimitExceeded);
		}
		return formatMessage(messages.unknownError);
	};

	const renderButtons = () => (
		<>
			<Button appearance="subtle" onClick={onClose}>
				{formatMessage(messages.closeButtonLabel)}
			</Button>
			{canRetry(error) && (
				<Button appearance="default" onClick={onTryAgain}>
					{formatMessage(messages.retryButtonLabel)}
				</Button>
			)}
		</>
	);

	return (
		<>
			<Inline space="space.100" alignBlock="center" xcss={styles.message}>
				<StatusErrorIcon
					color={token('color.icon.danger')}
					spacing="spacious"
					size="medium"
					label=""
				/>
				<span>{renderErrorMessage()}</span>
			</Inline>
			<Inline space="space.100" xcss={styles.actions}>
				{renderButtons()}
			</Inline>
		</>
	);
};

const canRetry = (error: InlineChatError | null) => {
	return (
		error?.message_template !== 'RATE_LIMIT' &&
		error?.message_template !== 'ACCEPTABLE_USE_VIOLATIONS'
	);
};
